import {Row, Col, Divider, Button, Checkbox} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {languages} from "../../../utils/helpers/constants";
import {getTranslations} from "../../../utils/helpers/translations";
import FieldBuilder from "../../form-components/field-builder";
import {FormProvider, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import {execute} from "../../../utils/helpers/execute";
import styles from "./style.module.scss";
import Spin from "../../general/antd/spin";
import {errorNotification, successNotification} from "../../../utils/helpers/notification";
import GeneralNotificationContext from "../../../context/general-notification/context";
import GeneralNotificationContextProvider from "../../../context/general-notification/provider";
import {IUpdateGeneralNotification} from "../../../models/general-notification/request";
import {getNotificationType} from "../../../models/general-notification/enum";
import EndPoints from "../../../services/end-points";
import {IProduct} from "../../../models/product/response";
import direction from "../../../utils/ui/direction";
import AppContext from "../../../context/app/context";
import ImageUploader from "../../form-components/image-uploader-client";
import {Controller} from "react-hook-form";
import Image from "../../general/antd/image";
import FormItem from "../../general/form-item";
import {MediumFor} from "../../../models/medium/enum";

const Form = () => {
    const {t} = useTranslation();
    const {details, actions, loading} = useContext(GeneralNotificationContext);
    const {id} = useParams();
    const navigate = useNavigate();

    const [products, setProducts] = useState<IProduct[]>([]);
    const [productLoading, setLoadingProduct] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const {direction} = useContext(AppContext);

    const [hasMore, setHasMore] = useState(true);
    const [query, setQuery] = useState({page: 1, search: undefined});

    // Image handling state
    const [newImage, setNewImage] = useState<string | null>(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [withoutBackground, setWithoutBackground] = useState(false);

    const methods = useForm<any>({
        defaultValues: {
            title: getTranslations(details?.translations?.title),
            note: getTranslations(details?.translations?.note),
            text: getTranslations(details?.translations?.text),
            slide: details?.slide,
            productId: details?.product?.id,
            imageId: details?.image?.id,
        } as IUpdateGeneralNotification,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        formState: {errors},
    } = methods;

    useEffect(() => {
        reset({
            title: getTranslations(details?.translations?.title),
            note: getTranslations(details?.translations?.note),
            text: getTranslations(details?.translations?.text),
            slide: details?.slide,
            productId: details?.product?.id,
            imageId: details?.image?.id,
        } as IUpdateGeneralNotification);

        // Initialize background state
        if (details?.image?.without_background !== undefined) {
            setWithoutBackground(details.image.without_background);
        }
    }, [details]);

    const handleScroll = (e) => {
        const {scrollTop, clientHeight, scrollHeight} = e.target;
        if (
            scrollHeight - scrollTop === clientHeight &&
            hasMore &&
            !productLoading
        ) {
            setQuery({...query, page: query.page + 1});
        }
    };

    const handleSearch = (value) => {
        setQuery({page: 1, search: value});
        setProducts([]);
    };

    // Image upload handlers - following product form pattern
    const handleUploadNewImage = async () => {
        if (!newImage) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        setImageLoading(true);

        try {
            const formData = new FormData();
            const response = await fetch(newImage);

            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            formData.append("medium", blob, "notification-image.jpg");
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            formData.append("without_background", withoutBackground ? "1" : "0");
            formData.append("is_notification", "1");

            const uploadRes = await EndPoints.media.uploadMedia(formData);
            setValue("imageId", uploadRes.id);
            setImageChanged(false); // Reset changed flag after successful upload
            successNotification(t("image_uploaded_successfully"));
        } catch (error) {
            console.error('Upload failed:', error);
            errorNotification(t("failed_to_upload_image"));
        } finally {
            setImageLoading(false);
        }
    };

    const handleUpdateBackground = async () => {
        if (details?.image?.id) {
            try {
                setImageLoading(true);
                await EndPoints.media.updateMedia(details.image.id, !withoutBackground, true);
                setWithoutBackground(!withoutBackground);
                successNotification(t("background_updated_successfully"));
            } catch (error) {
                errorNotification(t("failed_to_update_background"));
            } finally {
                setImageLoading(false);
            }
        }
    };

    const setWithoutBackgrounds = (value: boolean) => {
        setWithoutBackground(value);
    };

    // Handle removing existing image
    const handleRemoveExistingImage = async () => {
        setValue("imageId", null);
        setImageChanged(true);
        setNewImage(null);
        successNotification(t("image_removed"));
    };

    useEffect(() => {
        const getTrademarks = async () => {
            try {
                const {data} = await EndPoints.product.getAllProducts({
                    light: true,
                    perPage: -1,
                });
                setProducts(data.data);
            } catch (error) {
            } finally {
                setLoadingProduct(false);
            }
        };
        getTrademarks();
    }, [direction]);

    useEffect(() => {
        const getDetails = async () => {
            id && (await actions.getDetails(Number(id)));
        };
        getDetails();
    }, []);
    const onSubmit = async (data: any) => {
        console.log('Form data being submitted:', data);
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateGeneralNotification(details?.id, data)
                    : await actions.createGeneralNotification(data);
                navigate(-1);
            },
            fallback: (error) => {
                console.error('Form submission error:', error);
                errorNotification(t("failed_to_save"));
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
          <span className={styles.title}>
            {details ? t("update_general_notification") : t("add_general_notification")}
          </span>
                    <Divider className={styles.divider}/>
                    {loading.includes("details") ? (
                        <div style={{height: "50vh"}}>
                            <Spin/>
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`title[${lang?.code}]`}
                                                label={`${t("title")} (${lang?.name})`}
                                                input={{type: "text"}}
                                                rules={{required: true}}
                                            />
                                        </Col>
                                    );
                                })}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`text[${lang?.code}]`}
                                                label={`${t("text")} (${lang?.name})`}
                                                input={{type: "text-area"}}
                                                rules={{required: true}}
                                            />
                                        </Col>
                                    );
                                })}

                                <Col span={12}>
                                    <FieldBuilder
                                        name="slide"
                                        label={`${t("user_slide")}`}
                                        width="large"
                                        rules={{required: true}}
                                        input={{
                                            type: "select",
                                            loading: false,
                                            options: [1, 2, 3, 4, 5, 6, 7]?.map((type, index) => {
                                                return {
                                                    key: index,
                                                    label: t(getNotificationType(type)),
                                                    value: type,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="productId"
                                        label={`${t("product")}`}
                                        width="large"
                                        rules={{required: false}}
                                        input={{
                                            type: "select",
                                            loading: productLoading,
                                            allowSearch: true,
                                            options: products?.map((product, index) => {
                                                return {
                                                    key: index,
                                                    label: product.name,
                                                    value: product.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>

                                <Col span={24}>
                                    <FormItem label={t("image")}>
                                        <Controller
                                            name="imageId"
                                            control={control}
                                            render={({field: {ref, ...field}}) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    imageFor={MediumFor.UserProfile}
                                                    onChange={(response) => {
                                                        if (response) {
                                                            setNewImage(response.url);
                                                        } else {
                                                            setNewImage(null);
                                                        }
                                                        setImageChanged(true);
                                                        field.onChange(response?.id);
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>

                                {/* Delete button for existing image */}
                                {details?.image && !imageChanged && (
                                    <Col span={24}>
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined/>}
                                            onClick={handleRemoveExistingImage}
                                            style={{
                                                marginTop: 8,
                                                float: direction === 'rtl' ? 'left' : 'right',
                                            }}
                                        >
                                            {t("delete")}
                                        </Button>
                                    </Col>
                                )}

                                {/* Side-by-side image preview like product form */}
                                <div style={{
                                    marginTop: 16,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    gap: 32,
                                    width: "100%",
                                    marginInline: 16
                                }}>
                                    {details?.image && !imageChanged && (
                                        <div style={{textAlign: "center", flex: "1 1 300px"}}>
                                            <h4>{t("old_image")}</h4>
                                            <Image
                                                src={details.image.url}
                                                preview={true}
                                                style={{width: "100%", maxHeight: 300, objectFit: "contain"}}
                                                alt="Old Notification"
                                            />
                                        </div>
                                    )}

                                    {newImage && (
                                        <div style={{textAlign: "center", flex: "1 1 300px"}}>
                                            <h4>{t("new_image")}</h4>
                                            <Image
                                                src={newImage}
                                                preview={true}
                                                style={{width: "100%", maxHeight: 300, objectFit: "contain"}}
                                                alt="New Preview"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Upload button when image changed */}
                                {imageChanged && (
                                    <Col span={24}>
                                        <Button
                                            type="primary"
                                            onClick={handleUploadNewImage}
                                            style={{marginTop: 16, display: "block"}}
                                            loading={imageLoading}
                                        >
                                            {t("upload_new_image")}
                                        </Button>
                                    </Col>
                                )}

                                {/* Background checkbox like product form */}
                                <Col span={24}>
                                    <FormItem>
                                        <Checkbox
                                            checked={withoutBackground ?? false}
                                            onChange={(e) => {
                                                setWithoutBackgrounds(e.target.checked);
                                            }}
                                        >
                                            {t("without_background")}
                                        </Checkbox>
                                    </FormItem>
                                </Col>

                                <Col span={24}/>
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`note[${lang?.code}]`}
                                                label={`${t("note")} (${lang?.name})`}
                                                input={{type: "text-area"}}
                                                rules={{required: false}}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    )}
                </div>

                <div>
                    <Divider className={styles.divider}/>
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const GeneralNotificationForm = () => {
    return (
        <GeneralNotificationContextProvider>
            <Form/>
        </GeneralNotificationContextProvider>
    );
};
export default GeneralNotificationForm;
